import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  AccountType,
  RegistrationFormData,
  OcrExtraction,
  SignupState,
  GeoData,
  DocumentCapturePhase,
  SelectedDocumentType,
  FaceVerificationResult,
  PepDeclarationData,
  S3ImageKeys,
  ErutAnalysisResult,
  RepresentanteLegalData,
  PersonaJuridicaData,
  InformacionNegocioData,
} from '../interfaces/signup.interfaces';
import {
  STEPS_PERSONAL,
  STEPS_EMPRESA,
  STEPS_EMPRESA_LEGACY,
  PASOS_COMUNES,
  PASOS_EMPRESA,
  PASOS_PERSONA,
} from '../constants/signup.constants';
import { SignupPreviewService } from './signup-preview.service';
import { environment } from 'src/environments/environment';
import type { SendOtpCode } from 'src/app/auth/services/kyc-api.service';

const INITIAL_STATE: SignupState = {
  currentStep: 1,
  llaveReferidor: null,
  referidorNombre: null,
  accountType: null,
  formData: null,
  cognitoId: '',
  emailVerified: false,
  phoneVerified: false,
  whatsappOtpError: null,
  whatsappOtpErrorCode: null,
  ocrData: null,
  ocrCampos: null,
  selectedDocumentType: null,
  documentImage: '',
  documentImageFront: '',
  documentImageBack: '',
  documentCapturePhase: 'front',
  selfieImage: '',
  faceVerified: false,
  faceVerificationResult: null,
  geoData: null,
  // PEP
  politicamenteExpuesto: null,
  pepDeclarationData: null,
  // Empresa
  biometricConsent: false,
  erutAnalysis: null,
  representanteLegal: null,
  personaJuridica: null,
  informacionNegocio: null,
  // Rutas S3 de imágenes
  s3Keys: null,
  // OTP timestamps
  emailOtpSentAt: null,
  whatsappOtpExpiresAtMs: null,
  whatsappOtpResendAtMs: null,
};

// Duración de la transición en ms
const TRANSITION_DURATION = 400;

// Fallbacks del OTP de WhatsApp, usados SOLO si el backend no devolvió las
// duraciones. La política real vive en el KYC backend (`otp.expirationMinutes` y
// `whatsapp.cooldownPerNumberMs`); no replicar esos valores acá.
//
// Espejan la config vigente del KYC (5 min de vida, 60 s de cooldown). Si el
// fallback quedara CORTO, el front marcaría como expirado un código que el
// backend todavía acepta y le bloquearía el submit al usuario: ante la duda,
// pecar de largo.
const FALLBACK_OTP_TTL_SECONDS = 300;
const FALLBACK_OTP_RESEND_SECONDS = 60;

// Configuración de persistencia
const STORAGE_KEY = 'aguacate_signup_state';
const STORAGE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutos

interface PersistedState {
  state: SignupState;
  timestamp: number;
}

@Injectable()
export class SignupStateService {
  private state = new BehaviorSubject<SignupState>(INITIAL_STATE);
  private _isTransitioning = new BehaviorSubject<boolean>(false);

  constructor(private preview: SignupPreviewService) {
    // En preview el estado lo siembra el contenedor: restaurar lo pisaria.
    if (!this.preview.activo) {
      this.restoreState();
    }
  }

  // ==================== PERSISTENCIA ====================

  /**
   * Restaura el estado desde sessionStorage/localStorage
   */
  private restoreState(): void {
    try {
      // Intentar primero sessionStorage (misma pestaña)
      let stored = sessionStorage.getItem(STORAGE_KEY);

      // Si no hay en session, intentar localStorage (otra pestaña/cerró navegador)
      if (!stored) {
        stored = localStorage.getItem(STORAGE_KEY);
      }

      if (stored) {
        const persisted: PersistedState = JSON.parse(stored);
        const now = Date.now();

        // Verificar si no ha expirado
        if (now - persisted.timestamp < STORAGE_EXPIRY_MS) {
          // Restaurar estado (sin contraseña por seguridad)
          const restoredState = this.sanitizeRestoredState(persisted.state);
          this.state.next(restoredState);
        } else {
          this.clearPersistedState();
        }
      }
    } catch (err) {
      this.clearPersistedState();
    }
  }

  /**
   * Guarda el estado actual en storage
   */
  private persistState(): void {
    // El preview no persiste: si lo hiciera, el proximo registro de verdad
    // restauraria los datos de mentira y arrancaria con el modal de
    // "registro en progreso" de una empresa que no existe.
    if (this.preview.activo) {
      return;
    }

    try {
      const stateToPersist = this.sanitizeStateForStorage(this.currentState);
      const persisted: PersistedState = {
        state: stateToPersist,
        timestamp: Date.now(),
      };

      const serialized = JSON.stringify(persisted);

      // Guardar en ambos para cubrir todos los casos
      sessionStorage.setItem(STORAGE_KEY, serialized);
      localStorage.setItem(STORAGE_KEY, serialized);
    } catch (err) {
      // Error de persistencia no crítico
    }
  }

  /**
   * Limpia el estado persistido
   */
  clearPersistedState(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      // Error limpiando storage no crítico
    }
  }

  /**
   * Sanitiza el estado antes de guardar (elimina datos sensibles)
   */
  private sanitizeStateForStorage(state: SignupState): SignupState {
    const sanitized = { ...state };

    // Eliminar contraseña del formData
    if (sanitized.formData) {
      sanitized.formData = {
        ...sanitized.formData,
        password: '',
        repeatPassword: '',
      };
    }

    // Eliminar imágenes base64 para evitar exceder límite de localStorage
    // Las imágenes ya están subidas a S3 (s3Keys se mantienen)
    sanitized.documentImage = '';
    sanitized.documentImageFront = '';
    sanitized.documentImageBack = '';
    sanitized.selfieImage = '';

    // Eliminar fotoPersona del OCR (base64 grande)
    if (sanitized.ocrData?.fotoPersona) {
      sanitized.ocrData = {
        ...sanitized.ocrData,
        fotoPersona: '',
      };
    }

    return sanitized;
  }

  /**
   * Sanitiza el estado restaurado
   */
  private sanitizeRestoredState(state: SignupState): SignupState {
    // Si el usuario estaba en un paso que requiere revalidación, volver atrás
    if (state.currentStep > PASOS_COMUNES.TIPO_CUENTA && !state.accountType) {
      return { ...INITIAL_STATE };
    }

    if (state.accountType === 'empresas') {
      // Del paso del OCR en adelante hace falta la identidad capturada. Si ya
      // se subió a S3 (`s3Keys`), las base64 son descartables y se puede
      // retomar donde estaba; si no, hay que volver a capturar.
      if (state.currentStep >= PASOS_EMPRESA.OCR && state.s3Keys) {
        return {
          ...state,
          documentImage: '',
          documentImageFront: '',
          documentImageBack: '',
        };
      }

      if (state.currentStep >= PASOS_EMPRESA.OCR) {
        return {
          ...state,
          currentStep: PASOS_EMPRESA.OCR,
          documentImage: '',
          documentImageFront: '',
          documentImageBack: '',
          documentCapturePhase: 'front',
          selfieImage: '',
          faceVerified: false,
          faceVerificationResult: null,
          ocrData: null,
          ocrCampos: null,
        };
      }
      return state;
    }

    // Pasos 5-6 requieren imágenes base64 (eliminadas al persistir).
    // Volver al paso 5 para re-capturar documento (aplica Chile y Venezuela).
    if (
      state.currentStep === PASOS_PERSONA.OCR ||
      state.currentStep === PASOS_PERSONA.SELFIE
    ) {
      return {
        ...state,
        currentStep: PASOS_PERSONA.OCR,
        documentImage: '',
        documentImageFront: '',
        documentImageBack: '',
        documentCapturePhase: 'front',
        selfieImage: '',
        faceVerified: false,
        faceVerificationResult: null,
        ocrData: null,
        ocrCampos: null,
      };
    }

    // Paso 7+ sin s3Keys: las imágenes debieron subirse antes de llegar aquí.
    // Si faltan, volver a paso 5 para re-capturar.
    if (state.currentStep >= PASOS_PERSONA.CONFIRMACION && !state.s3Keys) {
      return {
        ...state,
        currentStep: PASOS_PERSONA.OCR,
        documentImage: '',
        documentImageFront: '',
        documentImageBack: '',
        documentCapturePhase: 'front',
        selfieImage: '',
        faceVerified: false,
        faceVerificationResult: null,
        ocrData: null,
        ocrCampos: null,
      };
    }

    return state;
  }

  // Observables del estado
  state$: Observable<SignupState> = this.state.asObservable();
  isTransitioning$ = this._isTransitioning.asObservable();

  // Getters
  get currentState(): SignupState {
    return this.state.getValue();
  }

  get currentStep(): number {
    return this.currentState.currentStep;
  }

  get accountType(): AccountType {
    return this.currentState.accountType;
  }

  /**
   * ¿Está encendido el flujo nuevo de empresas?
   *
   * Único punto de lectura del flag en todo el registro: los componentes
   * preguntan por acá y no importan `environment`. Con el flag apagado la
   * empresa recorre los 4 pasos de siempre y sale al dashboard.
   */
  get empresaFlujoNuevo(): boolean {
    return environment.empresaSignupV2Enabled === true;
  }

  get totalSteps(): number {
    if (this.currentState.accountType === 'empresas') {
      return this.empresaFlujoNuevo ? STEPS_EMPRESA : STEPS_EMPRESA_LEGACY;
    }
    // Persona: 8 pasos base, 9 si tiene vinculos PEP
    if (this.currentState.politicamenteExpuesto === true) {
      return STEPS_PERSONAL + 1; // 9 pasos
    }
    return STEPS_PERSONAL; // 8 pasos
  }

  get progressPercentage(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  get isPersonal(): boolean {
    return this.currentState.accountType === 'personal';
  }

  get isEmpresa(): boolean {
    return this.currentState.accountType === 'empresas';
  }

  // Getters de transición
  get isTransitioning(): boolean {
    return this._isTransitioning.value;
  }

  // Setters
  setAccountType(type: AccountType): void {
    this.updateState({ accountType: type });
  }

  setStep(step: number): void {
    this.updateState({ currentStep: step });
  }

  /**
   * Navega al siguiente paso con transición suave
   */
  nextStep(): Promise<void> {
    return this.navigateWithTransition(() => {
      const next = this.currentStep + 1;
      if (next <= this.totalSteps) {
        this.setStep(next);
      }
    });
  }

  /**
   * Navega al paso anterior con transición suave
   * Salta pasos de OTP ya verificados para evitar errores de código expirado
   */
  prevStep(): Promise<void> {
    return this.navigateWithTransition(() => {
      let prev = this.currentStep - 1;

      // Declaración PEP de persona: volver a la selección y resetear la
      // respuesta para que pueda cambiar de opinión. Es una regla del flujo de
      // persona y solo aplica ahí — en empresa ese mismo número es el e-RUT.
      if (this.isPersonal && this.currentStep === PASOS_PERSONA.PEP_DECLARACION) {
        this.resetPepData();
        prev = PASOS_PERSONA.PEP_SELECCION;
      }

      // Saltar pasos de OTP ya verificados
      if (prev === PASOS_COMUNES.OTP_WHATSAPP && this.currentState.phoneVerified) {
        prev = PASOS_COMUNES.OTP_EMAIL;
      }
      if (prev === PASOS_COMUNES.OTP_EMAIL && this.currentState.emailVerified) {
        prev = PASOS_COMUNES.FORMULARIO;
      }

      if (prev >= PASOS_COMUNES.TIPO_CUENTA) {
        this.setStep(prev);
        // Si vuelve al paso 1, resetear tipo de cuenta
        if (prev === PASOS_COMUNES.TIPO_CUENTA) {
          this.setAccountType(null);
        }
      }
    });
  }

  /**
   * Navega a un paso específico con transición
   */
  goToStep(step: number): Promise<void> {
    return this.navigateWithTransition(() => {
      if (step >= PASOS_COMUNES.TIPO_CUENTA && step <= this.totalSteps) {
        this.setStep(step);
      }
    });
  }

  /**
   * Ejecuta una navegación con transición suave
   */
  private navigateWithTransition(action: () => void): Promise<void> {
    return new Promise((resolve) => {
      // Iniciar transición (fade out)
      this._isTransitioning.next(true);

      // Esperar fade out, ejecutar acción, luego fade in
      setTimeout(() => {
        action();

        // Pequeña pausa antes del fade in
        setTimeout(() => {
          this._isTransitioning.next(false);
          resolve();
        }, 50);
      }, TRANSITION_DURATION / 2);
    });
  }

  setFormData(data: RegistrationFormData): void {
    this.updateState({ formData: data });
  }

  setCognitoId(id: string): void {
    this.updateState({ cognitoId: id });
  }

  /**
   * Guarda la llave de quien invito, leida de `?llave=` al entrar al wizard.
   *
   * Se guarda una sola vez: gana la primera. Si alguien entra con un link,
   * abandona y vuelve con otro, sigue contando el primero — la misma regla que
   * aplica el backend con su indice unico, replicada aca para que la pantalla
   * no le prometa al usuario un referidor que despues no va a quedar.
   */
  setLlaveReferidor(llave: string, nombre: string | null = null): void {
    if (this.currentState.llaveReferidor) return;
    this.updateState({ llaveReferidor: llave, referidorNombre: nombre });
  }

  setEmailVerified(verified: boolean): void {
    this.updateState({ emailVerified: verified });
  }

  setPhoneVerified(verified: boolean): void {
    this.updateState({ phoneVerified: verified });
  }

  setWhatsappOtpError(error: string | null, code: SendOtpCode | null = null): void {
    this.updateState({ whatsappOtpError: error, whatsappOtpErrorCode: code });
  }

  clearWhatsappOtpError(): void {
    this.updateState({ whatsappOtpError: null, whatsappOtpErrorCode: null });
  }

  setEmailOtpSentAt(timestamp: number | null): void {
    this.updateState({ emailOtpSentAt: timestamp });
  }

  /**
   * Traduce la respuesta de `/otp/send` o `/otp/resend` del KYC a deadlines en
   * el reloj del browser.
   *
   * Se usan las duraciones (`expiresInSeconds` / `resendInSeconds`) y no las
   * fechas absolutas del server para no depender de que el reloj del cliente
   * esté sincronizado. Si el backend no las manda (versión anterior), se cae a
   * los fallbacks y el control real lo sigue haciendo el backend.
   */
  applyWhatsappOtpResponse(response: {
    expiresInSeconds?: number;
    resendInSeconds?: number;
  }): void {
    const now = Date.now();
    const expiresIn = response.expiresInSeconds ?? FALLBACK_OTP_TTL_SECONDS;
    const resendIn = response.resendInSeconds ?? FALLBACK_OTP_RESEND_SECONDS;

    this.updateState({
      whatsappOtpExpiresAtMs: now + expiresIn * 1000,
      whatsappOtpResendAtMs: now + resendIn * 1000,
    });
  }

  /**
   * Aplica los deadlines de una respuesta RECHAZADA de `/otp/send` o
   * `/otp/resend`.
   *
   * El cooldown anti-ban del KYC responde `success: false` pero NO invalida el
   * código vigente: manda su `expiresInSeconds` junto con el `resendInSeconds`
   * que falta para poder pedir otro. Descartar esos valores dejaba al usuario
   * con un código bueno en el teléfono y el front marcándolo como vencido.
   *
   * A diferencia de `applyWhatsappOtpResponse`, acá no se cae a los fallbacks
   * y, además, solo se pisa lo que la respuesta trae. Que falte la vida del
   * código no prueba que no haya código vivo: el backend la omite también
   * cuando no la pudo leer, y el rechazo nunca reemplaza el código anterior.
   * Borrar el vencimiento guardado dejaba bloqueado, tras recargar, a quien
   * todavía tenía un código bueno en el teléfono. Si de verdad ya no vale, el
   * deadline viejo vence solo y el backend lo rechaza al verificar.
   */
  applyWhatsappOtpRejection(response: {
    expiresInSeconds?: number;
    resendInSeconds?: number;
  }): void {
    const now = Date.now();
    const cambios: Partial<SignupState> = {};
    if (response.expiresInSeconds != null) {
      cambios.whatsappOtpExpiresAtMs = now + response.expiresInSeconds * 1000;
    }
    if (response.resendInSeconds != null) {
      cambios.whatsappOtpResendAtMs = now + response.resendInSeconds * 1000;
    }
    this.updateState(cambios);
  }

  /**
   * Olvida el vencimiento del código actual sin tocar el cooldown de reenvío.
   * Para cuando el backend dijo que el código ya no sirve (vencido, bloqueado
   * por intentos o inexistente): así, al recargar, el paso no vuelve a mostrar
   * un countdown para un código que no se puede usar.
   */
  invalidateWhatsappOtpCode(): void {
    this.updateState({ whatsappOtpExpiresAtMs: null });
  }

  /** Olvida los deadlines del OTP de WhatsApp (envío fallido o número nuevo). */
  clearWhatsappOtpDeadlines(): void {
    this.updateState({
      whatsappOtpExpiresAtMs: null,
      whatsappOtpResendAtMs: null,
    });
  }

  setOcrData(data: OcrExtraction | null): void {
    this.updateState({ ocrData: data });
  }

  /**
   * Guarda los `campos` crudos del OCR (los que espera la consolidacion).
   *
   * Se llama junto a `setOcrData`, nunca en su lugar. Ver `ocrCampos` en
   * `signup.interfaces.ts` para por que son dos objetos y no uno.
   */
  setOcrCampos(campos: Record<string, any> | null | undefined): void {
    this.updateState({ ocrCampos: campos ?? null });
  }

  setDocumentImage(image: string): void {
    this.updateState({ documentImage: image });
  }

  setDocumentImageFront(image: string): void {
    this.updateState({ documentImageFront: image, documentImage: image });
  }

  setDocumentImageBack(image: string): void {
    this.updateState({ documentImageBack: image });
  }

  setDocumentCapturePhase(phase: DocumentCapturePhase): void {
    this.updateState({ documentCapturePhase: phase });
  }

  /**
   * Resetea las imagenes de documento para comenzar nueva captura
   */
  resetDocumentImages(): void {
    this.updateState({
      documentImage: '',
      documentImageFront: '',
      documentImageBack: '',
      documentCapturePhase: 'front',
    });
  }

  /**
   * Resetea todo lo relacionado con documentos (selección + imágenes)
   */
  resetDocumentStep(): void {
    this.updateState({
      selectedDocumentType: null,
      documentImage: '',
      documentImageFront: '',
      documentImageBack: '',
      documentCapturePhase: 'front',
      ocrData: null,
      ocrCampos: null,
    });
  }

  // Getters de documento
  get documentCapturePhase(): DocumentCapturePhase {
    return this.currentState.documentCapturePhase;
  }

  get needsBackCapture(): boolean {
    // Solo Chile necesita captura de reverso
    return this.documentType === 'chile';
  }

  get hasCompletedFrontCapture(): boolean {
    return !!this.currentState.documentImageFront;
  }

  get hasCompletedBackCapture(): boolean {
    return !!this.currentState.documentImageBack;
  }

  get hasCompletedAllDocumentCaptures(): boolean {
    if (this.needsBackCapture) {
      return this.hasCompletedFrontCapture && this.hasCompletedBackCapture;
    }
    return this.hasCompletedFrontCapture;
  }

  setSelfieImage(image: string): void {
    this.updateState({ selfieImage: image });
  }

  setFaceVerified(verified: boolean): void {
    this.updateState({ faceVerified: verified });
  }

  setFaceVerificationResult(result: FaceVerificationResult | null): void {
    this.updateState({ faceVerificationResult: result });
  }

  /**
   * Resetea la verificación facial para reintentar
   */
  resetFaceVerification(): void {
    this.updateState({
      selfieImage: '',
      faceVerified: false,
      faceVerificationResult: null,
    });
  }

  // Getters de verificación facial
  get fotoPersonaFromOcr(): string | null {
    return this.currentState.ocrData?.fotoPersona || null;
  }

  get hasFotoPersona(): boolean {
    return !!this.fotoPersonaFromOcr;
  }

  get needsManualReview(): boolean {
    return this.currentState.faceVerificationResult?.needsManualReview || false;
  }

  get faceVerified(): boolean {
    return this.currentState.faceVerified;
  }

  // ==================== PEP ====================

  setPoliticamenteExpuesto(value: boolean): void {
    this.updateState({ politicamenteExpuesto: value });
  }

  setPepDeclarationData(data: PepDeclarationData | null): void {
    this.updateState({ pepDeclarationData: data });
  }

  /**
   * Resetea los datos PEP
   */
  resetPepData(): void {
    this.updateState({
      politicamenteExpuesto: null,
      pepDeclarationData: null,
    });
  }

  get isPoliticamenteExpuesto(): boolean {
    return this.currentState.politicamenteExpuesto === true;
  }

  get hasPepSelection(): boolean {
    return this.currentState.politicamenteExpuesto !== null;
  }

  get pepDeclarationData(): PepDeclarationData | null {
    return this.currentState.pepDeclarationData;
  }

  // ==================== EMPRESA ====================

  setBiometricConsent(value: boolean): void {
    this.updateState({ biometricConsent: value });
  }

  get biometricConsent(): boolean {
    return this.currentState.biometricConsent;
  }

  setErutAnalysis(result: ErutAnalysisResult | null): void {
    this.updateState({ erutAnalysis: result });
  }

  get erutAnalysis(): ErutAnalysisResult | null {
    return this.currentState.erutAnalysis;
  }

  setRepresentanteLegal(data: RepresentanteLegalData | null): void {
    this.updateState({ representanteLegal: data });
  }

  get representanteLegal(): RepresentanteLegalData | null {
    return this.currentState.representanteLegal;
  }

  setPersonaJuridica(data: PersonaJuridicaData | null): void {
    this.updateState({ personaJuridica: data });
  }

  get personaJuridica(): PersonaJuridicaData | null {
    return this.currentState.personaJuridica;
  }

  setInformacionNegocio(data: InformacionNegocioData | null): void {
    this.updateState({ informacionNegocio: data });
  }

  get informacionNegocio(): InformacionNegocioData | null {
    return this.currentState.informacionNegocio;
  }

  // ==================== S3 KEYS ====================

  setS3Keys(keys: S3ImageKeys): void {
    this.updateState({ s3Keys: keys });
  }

  get s3Keys(): S3ImageKeys | null {
    return this.currentState.s3Keys;
  }

  get hasS3Keys(): boolean {
    return this.currentState.s3Keys !== null;
  }

  setGeoData(data: GeoData | null): void {
    this.updateState({ geoData: data });
  }

  // Getters de geolocalización
  get isChile(): boolean {
    return this.currentState.geoData?.detectedCountryIso === 'CHL';
  }

  get isVenezuela(): boolean {
    return this.currentState.geoData?.detectedCountryIso === 'VEN';
  }

  /**
   * Tipo de documento a usar:
   * - Empresa: siempre 'chile'
   * - Personal: lo que el usuario seleccionó
   */
  get documentType(): 'chile' | 'venezuela' | 'other' {
    // Empresa siempre usa documento chileno
    if (this.isEmpresa) return 'chile';

    // Personal usa la selección del usuario
    const selected = this.currentState.selectedDocumentType;
    if (selected) return selected;

    // Fallback a geolocalización si no hay selección
    if (this.isChile) return 'chile';
    if (this.isVenezuela) return 'venezuela';
    return 'other';
  }

  get selectedDocumentType(): SelectedDocumentType {
    return this.currentState.selectedDocumentType;
  }

  get hasSelectedDocumentType(): boolean {
    // Empresa no necesita seleccionar (siempre Chile)
    if (this.isEmpresa) return true;
    // Personal necesita que el usuario seleccione
    return this.currentState.selectedDocumentType !== null;
  }

  setSelectedDocumentType(type: SelectedDocumentType): void {
    this.updateState({ selectedDocumentType: type });
  }

  // Reset
  reset(): void {
    this.state.next(INITIAL_STATE);
    this.clearPersistedState();
    this.clearRegistrationInProgressFlag();
  }

  /**
   * Llamar cuando el registro se complete exitosamente
   * Limpia el estado y el flag de registro en progreso
   */
  completeRegistration(): void {
    this.clearPersistedState();
    this.clearRegistrationInProgressFlag();
    this.state.next(INITIAL_STATE);
  }

  /**
   * Cierre para salir al dashboard SIN resetear el paso visible. Limpia la
   * persistencia y el flag de registro-en-progreso (lo que el `AuthGuard`
   * chequea antes de dejar entrar a `/wallet`), pero NO hace
   * `state.next(INITIAL_STATE)`: ese reset mostraría el stepper en el paso 1
   * por un instante antes de que la navegación complete. El caller debe
   * navegar y recién después llamar `reset()`.
   */
  clearForExit(): void {
    this.clearPersistedState();
    this.clearRegistrationInProgressFlag();
  }

  /**
   * Limpia el flag de registro en progreso
   * Este flag evita que usuarios con sesión Cognito pero sin registro completo
   * accedan al dashboard
   */
  private clearRegistrationInProgressFlag(): void {
    try {
      sessionStorage.removeItem('aguacate_registration_in_progress');
    } catch (err) {
      // Error no crítico
    }
  }

  // Private helper
  private updateState(partial: Partial<SignupState>): void {
    this.state.next({
      ...this.currentState,
      ...partial,
    });

    // Persistir automáticamente después de cada cambio
    this.persistState();
  }
}
